import {
  type AlarmContactEntity,
  type AlarmContactId,
  type CallhomeCamera,
  type CHCameraId,
  type DeviceEntity,
  type DeviceId,
  type ScheduleState,
  type SiteId,
  type VMSCameraId,
} from '@solinkcorp/api-types';
import * as authentication from '@solinkcorp/authentication';
import { type UserSession } from '@solinkcorp/authentication';
import { type LocaleDefinition } from '@solinkcorp/locale';
import { toast } from '@solinkcorp/solink-components';
import { type WeeklySchedule } from '@solinkcorp/solink-schedule';
import { CallhomeApi, DeviceProxyApi, StorageAPI, VmsApi } from 'clients';
import { type RectCoordinates } from 'components/MotionGrid/DwellTimeRoi';
import { AppDispatcher } from 'dispatcher';
import get from 'lodash/get';
import merge from 'lodash/merge';
import AnalyticsLogger from 'models/AnalyticsLogger';
import { type CallHomePOS } from 'src/@solink/vd-data-layer/api-clients/callhome/types';
import { type SolinkDexie } from 'src/@solink/vd-data-layer/db';
import { isKiosk } from 'src/core/utils/kioskUtils';
import { PERSISTENT_PREFIX } from 'src/core/utils/StorageAPI';
import LocaleUtil from 'utils/LocaleUtil';
import { getAnyPresetDeviceId } from 'utils/RouteUtil';
import { type VideoDiscoverySite } from 'utils/types';
import {
  CAMERA_AI_DWELL_TIME_UPDATED_SUCCESS,
  CAMERA_AI_UPDATED_SUCCESS,
  CAMERA_MOTION_UPDATED_SUCCESS,
  CAMERA_NAME_UPDATED_FAILURE,
  CAMERA_NAME_UPDATED_SUCCESS,
  CAMERA_PTZ_ENABLED_UPDATED_SUCCESS,
  CREATE_LOCATION_ALARM_CONTACT,
  FAST_NOTIFICATION_DURATION,
  GET_LATEST_MOTION_SCHEDULE_INFO,
  GET_LOCATION_LIST,
  KIOSK_LOCATION,
  MOTION_TYPES,
  REMOVE_LOCATION_ALARM_CONTACT,
  STATUS_FAILED,
  STATUS_SUCCEEDED,
  UPDATE_CLOUD_CAMERA_RETENTION,
  UPDATE_DEVICE_TAGS,
  UPDATE_DEVICES,
  UPDATE_LOCATION_ALARM_CONTACT,
  UPDATE_LOCATION_MOTION_ALARM_SCHEDULE,
  UPDATE_LOCATION_TAGS,
  UPDATE_LOCATIONS_TAGS,
  UPDATE_MULTI_SITE_DEVICES_FEATURES,
} from 'vd-constants';

export async function getLocations(db: SolinkDexie, user: UserSession) {
  const selectedLocationId = getAnyPresetDeviceId();
  const kioskLocationId = StorageAPI.getLocally(PERSISTENT_PREFIX + KIOSK_LOCATION);
  if (!selectedLocationId && kioskLocationId) {
    AnalyticsLogger.selectedLocationIdNotAvailableKiosk();
  }

  // If there is a pre selected location ID in place (in URL query OR local storage)
  // only fetch that location to save bandwidth/time
  const shouldPreFetchOneLocation = !!(selectedLocationId || kioskLocationId);
  const filter = shouldPreFetchOneLocation
    ? { where: { id: selectedLocationId || kioskLocationId } }
    : undefined;

  // Don't use indexedDB locations cache when we only pre-fetch one location and use it first
  // And don't cache or use cache for Kiosk mode
  const shouldCache = !shouldPreFetchOneLocation && !isKiosk();
  // If there is cached locations in indexedDB, use that first
  const cachedLocationsDb = db.locations;
  if (cachedLocationsDb && shouldCache) {
    try {
      const cachedLocations = await db.locations.toArray();
      // Locations saved in cache contains the all-locations entry.
      // So if user don't have access to any locations, there should still be
      // one location (all-locations) in the cache.
      if (cachedLocations?.length > 0) {
        AppDispatcher.dispatch(GET_LOCATION_LIST, {
          payload: { user, results: cachedLocations, shouldCache: false, dbId: db.id },
        });
        console.debug('Fetched locations from indexedDB');
      }
    } catch (err) {
      console.error('Error fetching locations from indexedDB', err);
    }
  }

  // for the analytics of the network request
  const requestStart = performance.now();
  let requestTimeInSeconds = 0;
  let numberOfLocations, statusCode, responseUrl;

  try {
    // TODO with pagination - it would be better to get a single site - rather than using sites/query with an id filter
    const response = await CallhomeApi(user.accessToken).getLocations({ filter, timeout: 0 });

    requestTimeInSeconds = Math.round((performance.now() - requestStart) / 1000);
    numberOfLocations = response.total;
    statusCode = response.statusCode;
    responseUrl = response.request?.responseURL;

    AppDispatcher.dispatch(GET_LOCATION_LIST, {
      payload: { user, ...response, shouldCache, dbId: db.id },
    });

    // If there is a pre selected location ID from the launch video URL query
    // fetch all locations after the first one location fetch
    if (shouldPreFetchOneLocation && !kioskLocationId) {
      // for the analytics of the network request
      const allLocsRequestStart = performance.now();
      let allLocsRequestTimeInSeconds = 0;
      let allLocsNumberOfLocations, allLocsStatusCode, allLocsResponseUrl;

      try {
        const response = await CallhomeApi(user.accessToken).getLocations({ timeout: 0 });

        allLocsRequestTimeInSeconds = Math.round((performance.now() - allLocsRequestStart) / 1000);
        allLocsNumberOfLocations = response.total;
        allLocsStatusCode = response.statusCode;
        allLocsResponseUrl = response.request?.responseURL;

        AppDispatcher.dispatch(GET_LOCATION_LIST, {
          payload: { user, ...response, shouldCache, dbId: db.id },
        });
      } catch (err: any) {
        allLocsRequestTimeInSeconds = Math.round((performance.now() - allLocsRequestStart) / 1000);
        allLocsStatusCode = err.response?.status;
        allLocsResponseUrl = err.response?.request?.responseURL;
        console.error('Error fetching locations', err);
        authentication.logout('LOCATIONS_API_ERROR');
      } finally {
        AnalyticsLogger.callLocationsApi({
          numberOfLocations: allLocsNumberOfLocations,
          // If time is less than 1 second, set time to 1 second
          durationOfNetworkRequest: `${
            allLocsRequestTimeInSeconds === 0 ? 1 : allLocsRequestTimeInSeconds
          }s`,
          statusCode: allLocsStatusCode,
          url: allLocsResponseUrl,
          sizeOfResponseBody: -1,
        });
      }
    }
  } catch (err: any) {
    requestTimeInSeconds = Math.round((performance.now() - requestStart) / 1000);
    statusCode = err.response?.status;
    responseUrl = err.response?.request?.responseURL;
    console.error('Error fetching locations', err);
    authentication.logout('LOCATIONS_API_ERROR');
  } finally {
    let sizeOfResponseBody;
    AnalyticsLogger.callLocationsApi({
      numberOfLocations,
      // If time is less than 1 second, set time to 1 second
      durationOfNetworkRequest: `${requestTimeInSeconds === 0 ? 1 : requestTimeInSeconds}s`,
      statusCode,
      url: responseUrl,
      sizeOfResponseBody,
    });
  }
}

export function setCameraName({
  deviceId,
  cameraId,
  name,
  uniqueId,
  callback,
}: {
  deviceId: DeviceId;
  cameraId: VMSCameraId;
  name: string;
  uniqueId: CHCameraId;
  callback?: () => void;
}) {
  // Use the camera UUID
  CallhomeApi()
    .updateCamera(uniqueId, { overrideName: name })
    .then(() => {
      AppDispatcher.dispatch(CAMERA_NAME_UPDATED_SUCCESS, {
        payload: {
          deviceId,
          cameraId,
          uniqueId,
          name,
        },
      });
      callback?.();
    })
    .catch((err) => {
      console.error('Error', err);
      AppDispatcher.dispatch(CAMERA_NAME_UPDATED_FAILURE, {
        msg: err.message,
      });
      callback?.();
    });
}

export async function closeAlarm(siteId: SiteId, eventId: string) {
  return await CallhomeApi().closeAlarm(siteId, eventId);
}

type MotionParamsTemp = NonNullable<CallhomeCamera['motionParams']>;

export async function setCameraMotionParams({
  cameraId,
  deviceId,
  deviceUrl,
  motionParams,
  uniqueId,
  useProxy = false,
}: {
  cameraId: VMSCameraId;
  deviceId: DeviceId;
  deviceUrl: string;
  motionParams: Pick<MotionParamsTemp, 'enabled' | 'roi' | 'eventThreshold'>;
  uniqueId: CHCameraId;
  useProxy?: boolean;
}) {
  try {
    const response = useProxy
      ? await DeviceProxyApi().putCameraMotion(deviceId, cameraId, motionParams)
      : await VmsApi(deviceUrl).putCameraMotion(cameraId, motionParams);
    await CallhomeApi().updateCamera(uniqueId, { motionParams });

    AppDispatcher.dispatch(CAMERA_MOTION_UPDATED_SUCCESS, {
      payload: {
        deviceId,
        cameraId,
        motionParams,
        uniqueId,
      },
    });

    return response;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function setCameraAi({
  ai,
  cameraId,
  deviceId,
  deviceUrl,
  uniqueId,
}: {
  ai?: {
    detectionType: string;
    enabled: boolean;
    roi?: RectCoordinates | null | undefined;
  };
  cameraId: VMSCameraId;
  deviceId: DeviceId;
  deviceUrl: string;
  uniqueId: CHCameraId;
}) {
  try {
    // update VMS to take effect immediately (no wait for checkin)
    await VmsApi(deviceUrl).putCameraAi({ camId: cameraId, aiParams: ai });
    // update Callhome so the camera list page shows the updated count
    await CallhomeApi().updateCamera(uniqueId, { ai });

    AppDispatcher.dispatch(CAMERA_AI_UPDATED_SUCCESS, {
      payload: {
        ai,
        deviceId,
        cameraId,
        uniqueId,
      },
    });
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function setCameraPtzEnabled({
  isPtzEnabled,
  locationId,
  uniqueId,
}: {
  isPtzEnabled: boolean;
  locationId: SiteId;
  uniqueId: CHCameraId;
}) {
  try {
    await CallhomeApi().updateCamera(uniqueId, { isPtzEnabled });

    AppDispatcher.dispatch(CAMERA_PTZ_ENABLED_UPDATED_SUCCESS, {
      payload: {
        isPtzEnabled,
        locationId,
        uniqueId,
      },
    });
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function setCameraAiDwellTime({
  aiDwellTime,
  cameraId,
  deviceId,
  deviceUrl,
  uniqueId,
}: {
  aiDwellTime: {
    enabled: boolean;
    rois: {
      roi_id?: string;
      metadata?: string;
      roi: [number, number, number, number];
    }[];
  };
  cameraId: VMSCameraId;
  deviceId: DeviceId;
  deviceUrl: string;
  uniqueId: CHCameraId;
}) {
  try {
    // update VMS to take effect immediately (no wait for checkin)
    await VmsApi(deviceUrl).postCameraAiDwellTime(cameraId, aiDwellTime);
    const { camera } = await VmsApi(deviceUrl, false).getCameraJson(cameraId);

    AppDispatcher.dispatch(CAMERA_AI_DWELL_TIME_UPDATED_SUCCESS, {
      payload: {
        aiDwellTime,
        deviceId,
        cameraId,
        uniqueId,
      },
    });
    if (camera && camera.aiSettings) {
      return camera.aiSettings;
    }
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function updateMotionPosCameraAiFilter({
  ai,
  cameraId,
  motionPosDevices,
}: {
  ai: {
    detectionType: string;
    enabled: boolean;
    roi: RectCoordinates | null | undefined;
  };
  cameraId: VMSCameraId;
  motionPosDevices: CallHomePOS[] | undefined; // TODO: Confirm if motionPosDevices can be undefined
  // I assume it can be because updateMotionPosCameraAiFilter seems to iterate across an empty array if it is undefined/null
}) {
  const shouldFilterBeEnabled = ai.enabled && ai.detectionType !== MOTION_TYPES.DWELL_TIME;
  // motion parsers update: add or remove the camera to `cameraAiFilter`
  // depending if user desire ai motion alert (of any kind) or regular motion alerts.
  for (const posDevice of motionPosDevices || []) {
    const cameraAiFilter = get(posDevice, 'metadata.MOTION.cameraAiFilter', []);
    const aiFilterDiscardsEvents = get(posDevice, 'metadata.MOTION.aiFilterDiscardsEvents', true);
    const currCamIdx = cameraAiFilter.indexOf(cameraId);
    let changed = false;
    if (shouldFilterBeEnabled && currCamIdx < 0) {
      cameraAiFilter.push(cameraId);
      changed = true;
    } else if (!shouldFilterBeEnabled && currCamIdx >= 0) {
      cameraAiFilter.splice(currCamIdx, 1);
      changed = true;
    }
    if (changed) {
      const newMetadata = merge({}, posDevice.metadata, {
        MOTION: {
          cameraAiFilter,
          aiFilterDiscardsEvents: aiFilterDiscardsEvents || cameraAiFilter.length > 0,
        },
      });
      await CallhomeApi().patchPOSDevice(posDevice.id, {
        metadata: newMetadata,
      });
    }
  }
}

/**
 * @param {object} params
 */
export async function setLocationMotionAlarmSchedule({
  cameraId,
  locationId,
  muteUntil,
  schedule,
  state,
}: {
  cameraId: CHCameraId | undefined;
  locationId: SiteId;
  muteUntil: number;
  schedule: WeeklySchedule;
  state: ScheduleState;
}) {
  let camera;
  const resSchedule = await CallhomeApi().setMotionAlarmScheduleForLocation({
    locationId,
    muteUntil,
    schedule,
    state,
  });
  if (cameraId) {
    camera = await CallhomeApi().enableMotionAlarmsOnCamera(cameraId);
  }
  AppDispatcher.dispatch(UPDATE_LOCATION_MOTION_ALARM_SCHEDULE, {
    camera,
    locationId,
    schedule: resSchedule,
  });

  return resSchedule;
}

export async function createLocationAlarmContact({
  locationId,
  data,
}: {
  locationId: SiteId;
  data: Partial<AlarmContactEntity>;
}) {
  const alarmContact = await CallhomeApi().createNewAlarmContact(locationId, data);
  AppDispatcher.dispatch(CREATE_LOCATION_ALARM_CONTACT, {
    locationId,
    alarmContact,
  });

  return alarmContact;
}

export type UpdateLocationAlarmContactParams = {
  locationId: SiteId;
  alarmContactId: AlarmContactId;
  data: Partial<AlarmContactEntity>;
};

export const updateLocationAlarmContact: (
  params: UpdateLocationAlarmContactParams,
) => Promise<AlarmContactEntity> = async ({ locationId, alarmContactId, data }) => {
  const alarmContact = await CallhomeApi().updateNewAlarmContact(locationId, alarmContactId, data);

  AppDispatcher.dispatch(UPDATE_LOCATION_ALARM_CONTACT, {
    locationId,
    alarmContact,
  });
  // TODO: Analytics logging

  return alarmContact;
};

export async function removeLocationAlarmContact({
  locationId,
  alarmContactId,
}: {
  locationId: SiteId;
  alarmContactId: AlarmContactId;
}) {
  const alarmContact = await CallhomeApi().removeAlarmContact(locationId, alarmContactId);
  AppDispatcher.dispatch(REMOVE_LOCATION_ALARM_CONTACT, {
    locationId,
    alarmContact,
  });
  // TODO: Analytics logging
  return alarmContact;
}

export async function resetAlarmContactAccessCode({
  locationId,
  alarmContactId,
  data,
}: {
  locationId: SiteId;
  alarmContactId: AlarmContactId;
  data: {
    email?: string;
    phoneNumber?: string;
    name?: string;
    priority?: number;
    expiresAt?: number;
  };
}) {
  const alarmContact = await CallhomeApi().resetAlarmContactAccessCode(
    locationId,
    alarmContactId,
    data,
  );

  AppDispatcher.dispatch(UPDATE_LOCATION_ALARM_CONTACT, {
    locationId,
    alarmContact,
  });

  return alarmContact;
}

/**
 * Alarm V2
 * Given a `locationId`, this function makes two API requests to get the latest motion schedule info
 * from both the `cameras` and `locations` endpoints and combines the results together before dispatching
 * the results.
 */
export async function getLatestAlarmInfo(siteId: SiteId) {
  try {
    const cameras = await CallhomeApi().getLocationCamerasWithSchedule({ siteId });
    const location = await CallhomeApi().getLocationWithAlarmInfo({ siteId });
    if (cameras.length > 0 && !!location) {
      location.cameras = cameras;
      AppDispatcher.dispatch(GET_LATEST_MOTION_SCHEDULE_INFO, { location });
    } else {
      console.warn('Unable to find cameras or location with id: ', siteId);
    }
  } catch (err) {
    console.error(err);
  }
}

export async function updateMultipleLocationsTags({
  locations,
  locale,
}: {
  locations: Pick<VideoDiscoverySite, 'id' | 'name' | 'locationTags'>[];
  locale: LocaleDefinition;
}) {
  const locationTagsAnalytics = locations.map(
    (location) =>
      `${location.name}: (${
        location.locationTags?.length ? location.locationTags.join(', ') : 'No Tags'
      })`,
  );
  try {
    const newLocations = await CallhomeApi().updateMultipleLocationsTags(locations);
    AppDispatcher.dispatch(UPDATE_LOCATIONS_TAGS, { locations: newLocations });
    toast.success(locale.locationTagging.updateLocationsTagsSuccess, {
      autoClose: FAST_NOTIFICATION_DURATION,
    });
    AnalyticsLogger.bulkAssignLocationTags({
      status: STATUS_SUCCEEDED,
      locationTags: locationTagsAnalytics,
    });
  } catch (err: any) {
    toast.error(err);
    AnalyticsLogger.bulkAssignLocationTags({
      status: STATUS_FAILED,
      locationTags: locationTagsAnalytics,
    });
  }
}

export async function updateLocationTags({
  location,
  locale,
}: {
  location: VideoDiscoverySite;
  locale: LocaleDefinition;
}) {
  try {
    const newLocation = await CallhomeApi().updateLocationTags(location);
    AppDispatcher.dispatch(UPDATE_LOCATION_TAGS, { location: newLocation });
    toast.success(
      LocaleUtil.getLocalizedStr({
        string: locale.locationTagging.updateLocationTagsSuccess,
        replacements: { location: location.name },
      }),
      {
        autoClose: FAST_NOTIFICATION_DURATION,
      },
    );
    AnalyticsLogger.assignLocationTagsToLocation({
      status: STATUS_SUCCEEDED,
      location: location.name,
      locationTags: location.locationTags,
    });
  } catch (err: any) {
    toast.error(err);
    AnalyticsLogger.assignLocationTagsToLocation({
      status: STATUS_FAILED,
      location: location.name,
      locationTags: location.locationTags,
    });
  }
}

export async function updateDeviceTags({
  siteId,
  deviceId,
  tags,
}: {
  siteId: string;
  deviceId: string;
  tags: string[];
}) {
  await CallhomeApi().patchDeviceEntity(deviceId, { tags });
  AppDispatcher.dispatch(UPDATE_DEVICE_TAGS, { siteId, deviceId, tags });
}

// Throwing variant. Use this when the caller must know the PATCH landed before it
// continues, for example before asking a backend service to read the config it wrote.
export async function updateLocationAccessOrThrow(
  siteId: SiteId,
  devices: any,
  deviceId: DeviceId,
  deviceFeatures: any,
) {
  await CallhomeApi().patchDeviceEntity(deviceId, { features: deviceFeatures });
  AppDispatcher.dispatch(UPDATE_DEVICES, {
    siteId,
    devices,
  });
}

export async function updateLocationAccess(
  siteId: SiteId,
  devices: any,
  deviceId: DeviceId,
  deviceFeatures: any,
) {
  try {
    await updateLocationAccessOrThrow(siteId, devices, deviceId, deviceFeatures);
  } catch (err) {
    console.error(err);
  }
}

export async function updateDevicesFeatures(siteId: SiteId, devices: DeviceEntity[]) {
  try {
    for (const device of devices) {
      await CallhomeApi().patchDeviceEntity(device.id, { features: device.features });
    }
    AppDispatcher.dispatch(UPDATE_DEVICES, {
      siteId,
      devices,
    });
  } catch (err) {
    console.error(err);
  }
}

export async function bulkUpdateDevicesFeatures(sites: {
  [siteId: SiteId]: {
    devices: DeviceEntity[];
  };
}) {
  try {
    const devicesWithFeatures: { id: DeviceId; features: DeviceEntity['features'] }[] =
      Object.values(sites)
        .flatMap(({ devices }) => devices)
        .map((device) => ({
          id: device.id,
          features: device.features,
        }));
    await CallhomeApi().bulkUpdateDevicesFeatures(devicesWithFeatures);
    AppDispatcher.dispatch(UPDATE_MULTI_SITE_DEVICES_FEATURES, {
      sites,
    });
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export function updateLocationsCameras(locations: VideoDiscoverySite[]) {
  // TODO: Setup API call to update location cameras
  AppDispatcher.dispatch(UPDATE_CLOUD_CAMERA_RETENTION, { locations });
}
